import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import PageHeader from "../components/PageHeader";
import Modal from "../components/Modal";
import { formatBytes, formatDate } from "../utils/format";

const PLACEHOLDERS = [
  "company",
  "job_title",
  "hiring_manager",
  "why_company",
  "relevant_experience",
  "my_name",
];
const EMAIL_PLACEHOLDERS = [
  "company",
  "job_title",
  "recipient_name",
  "my_name",
  "relevant_experience",
  "why_company",
];
export const EMAIL_KIND_LABELS = {
  recruiter: "Recruiter",
  hiringManager: "Hiring manager",
  team: "Senior peer",
};

function DocRow({
  doc,
  icon,
  meta,
  onDefault,
  onRemove,
  onOpen,
  onDownload,
  onEdit,
}) {
  const details = (
    <>
      <div className="fw-medium text-truncate">{doc.name}</div>
      <div className="small text-muted">
        {doc.isDefault && (
          <span className="badge bg-primary-subtle text-primary-emphasis me-1">
            Default
          </span>
        )}
        {meta}
      </div>
    </>
  );

  return (
    <div className="doc-row">
      <span className="doc-icon">
        <i className={`bi ${icon}`} />
      </span>
      {onOpen ? (
        <button
          className="btn btn-link p-0 text-start text-body text-decoration-none flex-grow-1 min-w-0"
          onClick={onOpen}
        >
          {details}
        </button>
      ) : (
        <div className="flex-grow-1 min-w-0">{details}</div>
      )}
      {onEdit && (
        <button className="btn btn-sm btn-light text-nowrap" onClick={onEdit}>
          <i className="bi bi-pencil me-1" /> Edit
        </button>
      )}
      {onDefault && !doc.isDefault && (
        <button
          className="btn btn-sm btn-link text-muted d-none d-sm-inline text-nowrap"
          onClick={onDefault}
        >
          Make default
        </button>
      )}
      <div className="dropdown">
        <button
          className="btn btn-sm btn-light"
          data-bs-toggle="dropdown"
          aria-label="More"
        >
          <i className="bi bi-three-dots" />
        </button>
        <ul className="dropdown-menu dropdown-menu-end">
          {onDownload && (
            <li>
              <button className="dropdown-item" onClick={onDownload}>
                Download
              </button>
            </li>
          )}
          {onDefault && !doc.isDefault && (
            <li>
              <button className="dropdown-item" onClick={onDefault}>
                Make default
              </button>
            </li>
          )}
          <li>
            <button className="dropdown-item text-danger" onClick={onRemove}>
              Remove
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}

function Documents() {
  const {
    documents,
    uploadResume,
    downloadResume,
    uploadTranscript,
    downloadTranscript,
    addTemplate,
    addEmailTemplate,
    updateTemplate,
    updateEmailTemplate,
    removeDocument,
    setDefaultDocument,
    toast,
  } = useApp();
  const { hash } = useLocation();
  const resumeInput = useRef(null);
  const transcriptInput = useRef(null);
  const templateInput = useRef(null);
  const [editor, setEditor] = useState(null);
  const [emailEditor, setEmailEditor] = useState(null);
  const [savingEmailTemplate, setSavingEmailTemplate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingTranscript, setUploadingTranscript] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    if (hash)
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  }, [hash]);

  const onResume = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    if (await uploadResume(file)) toast(`Uploaded ${file.name}`);
    setUploading(false);
  };

  const onTranscript = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingTranscript(true);
    if (await uploadTranscript(file)) toast(`Uploaded ${file.name}`);
    setUploadingTranscript(false);
  };

  const onTemplateFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const content = file.type.startsWith("text/") ? await file.text() : "";
    setEditor({ name: file.name.replace(/\.[^.]+$/, ""), content });
    e.target.value = "";
  };

  const saveTemplate = async () => {
    if (!editor.name.trim() || !editor.content.trim()) return;
    setSavingTemplate(true);
    const changes = { name: editor.name.trim(), content: editor.content };
    const saved = editor.id
      ? await updateTemplate(editor.id, changes)
      : await addTemplate(changes);
    setSavingTemplate(false);
    if (!saved) return;
    toast(editor.id ? "Template updated" : "Template saved");
    setEditor(null);
  };

  const saveEmailTemplate = async () => {
    if (!emailEditor.name.trim() || !emailEditor.content.trim()) return;
    if (emailEditor.attachResume && !emailEditor.resumeId) return;
    setSavingEmailTemplate(true);
    const changes = {
      name: emailEditor.name.trim(),
      content: emailEditor.content,
      kind: emailEditor.kind,
      attachResume: Boolean(emailEditor.attachResume),
      resumeId: emailEditor.attachResume ? emailEditor.resumeId : null,
    };
    const saved = emailEditor.id
      ? await updateEmailTemplate(emailEditor.id, changes)
      : await addEmailTemplate(changes);
    setSavingEmailTemplate(false);
    if (!saved) return;
    toast(
      emailEditor.id ? "Outreach template updated" : "Outreach template saved",
    );
    setEmailEditor(null);
  };

  const emailTemplates = documents.emailTemplates || [];

  return (
    <>
      <PageHeader
        title="Documents"
        subtitle="Upload these once. We use them to tailor your resume, write cover letters and fill in applications."
      />

      <div className="card mb-4">
        <div className="card-header-lite">
          <div>
            <h2 className="h6 mb-0">Resumes</h2>
            <div className="small text-muted">
              Your default resume is the starting point for tailoring. Upload
              the Word (.docx) version to get tailored PDFs in your exact
              format.
            </div>
          </div>
          <button
            className="btn btn-sm btn-primary flex-shrink-0"
            onClick={() => resumeInput.current.click()}
            disabled={uploading}
          >
            {uploading ? (
              <span className="spinner-border spinner-border-sm me-1" />
            ) : (
              <i className="bi bi-upload me-1" />
            )}
            {uploading ? "Uploading…" : "Upload"}
          </button>
          <input
            ref={resumeInput}
            type="file"
            accept=".pdf,.doc,.docx"
            hidden
            onChange={onResume}
          />
        </div>
        {documents.resumes.length === 0 ? (
          <button
            className="dropzone"
            onClick={() => resumeInput.current.click()}
          >
            <i className="bi bi-file-earmark-arrow-up fs-3 d-block mb-2" />
            <span className="fw-medium">Upload your resume</span>
            <span className="small text-muted d-block">
              PDF or Word, up to 5 MB
            </span>
          </button>
        ) : (
          <div className="list-flush">
            {documents.resumes.map((r) => (
              <DocRow
                key={r.id}
                doc={r}
                icon="bi-file-earmark-pdf"
                meta={
                  r.hasFile && !r.hasText ? (
                    <span className="text-warning-emphasis">
                      <i className="bi bi-exclamation-triangle me-1" />
                      Text not readable, so it can't be tailored. Upload a PDF
                      or DOCX exported from your editor.
                    </span>
                  ) : (
                    `${formatBytes(r.size)} · Uploaded ${formatDate(r.uploadedAt)}`
                  )
                }
                onDefault={() => setDefaultDocument("resumes", r.id)}
                onRemove={() => removeDocument("resumes", r.id)}
                onDownload={r.hasFile ? () => downloadResume(r) : undefined}
              />
            ))}
          </div>
        )}
      </div>

      <div className="card mb-4" id="transcript">
        <div className="card-header-lite">
          <div>
            <h2 className="h6 mb-0">Transcript</h2>
            <div className="small text-muted">
              Keep it handy for applications that ask for one.
            </div>
          </div>
          <button
            className="btn btn-sm btn-primary flex-shrink-0"
            onClick={() => transcriptInput.current.click()}
            disabled={uploadingTranscript}
          >
            {uploadingTranscript ? (
              <span className="spinner-border spinner-border-sm me-1" />
            ) : (
              <i className="bi bi-upload me-1" />
            )}
            {uploadingTranscript ? "Uploading…" : "Upload"}
          </button>
          <input
            ref={transcriptInput}
            type="file"
            accept=".pdf,.doc,.docx"
            hidden
            onChange={onTranscript}
          />
        </div>
        {documents.transcripts.length === 0 ? (
          <button
            className="dropzone"
            onClick={() => transcriptInput.current.click()}
          >
            <i className="bi bi-mortarboard fs-3 d-block mb-2" />
            <span className="fw-medium">Upload your transcript</span>
            <span className="small text-muted d-block">
              PDF or Word, up to 5 MB. Unofficial transcripts are usually fine.
            </span>
          </button>
        ) : (
          <div className="list-flush">
            {documents.transcripts.map((t) => (
              <DocRow
                key={t.id}
                doc={t}
                icon="bi-mortarboard"
                meta={`${formatBytes(t.size)} · Uploaded ${formatDate(t.uploadedAt)}`}
                onRemove={() => removeDocument("transcripts", t.id)}
                onDownload={() => downloadTranscript(t)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="card" id="templates">
        <div className="card-header-lite">
          <div>
            <h2 className="h6 mb-0">Cover letter templates</h2>
            <div className="small text-muted">
              Write it in your voice. We fill in the job-specific parts.
            </div>
          </div>
          <div className="d-flex gap-2 flex-shrink-0">
            <button
              className="btn btn-sm btn-light"
              onClick={() => templateInput.current.click()}
            >
              <i className="bi bi-upload me-1" />
              <span className="d-none d-sm-inline">Upload</span>
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => setEditor({ name: "", content: "" })}
            >
              <i className="bi bi-plus-lg me-1" /> New
            </button>
          </div>
          <input
            ref={templateInput}
            type="file"
            accept=".txt,.md,.doc,.docx"
            hidden
            onChange={onTemplateFile}
          />
        </div>
        {documents.templates.length === 0 ? (
          <button
            className="dropzone"
            onClick={() => setEditor({ name: "", content: "" })}
          >
            <i className="bi bi-envelope-paper fs-3 d-block mb-2" />
            <span className="fw-medium">Add your cover letter template</span>
            <span className="small text-muted d-block">
              Paste it in or upload a file
            </span>
          </button>
        ) : (
          <div className="list-flush">
            {documents.templates.map((t) => (
              <DocRow
                key={t.id}
                doc={t}
                icon="bi-file-earmark-text"
                meta={`Added ${formatDate(t.uploadedAt)} · ${t.content.split(/\s+/).length} words`}
                onDefault={() => setDefaultDocument("templates", t.id)}
                onRemove={() => removeDocument("templates", t.id)}
                onOpen={() => setEditor({ ...t, readOnly: true })}
                onEdit={() => setEditor({ ...t })}
              />
            ))}
          </div>
        )}
      </div>

      <div className="card mt-4" id="email-templates">
        <div className="card-header-lite">
          <div>
            <h2 className="h6 mb-0">Outreach email templates</h2>
            <div className="small text-muted">
              Your voice for cold emails - one style for recruiters, one for
              hiring managers, one for senior peers. We rewrite the matching
              template for each job and person.
            </div>
          </div>
          <button
            className="btn btn-sm btn-primary flex-shrink-0"
            onClick={() =>
              setEmailEditor({
                name: "",
                content: "",
                kind: "recruiter",
                attachResume: false,
                resumeId: "",
              })
            }
          >
            <i className="bi bi-plus-lg me-1" /> New
          </button>
        </div>
        {emailTemplates.length === 0 ? (
          <button
            className="dropzone"
            onClick={() =>
              setEmailEditor({
                name: "",
                content: "",
                kind: "recruiter",
                attachResume: false,
                resumeId: "",
              })
            }
          >
            <i className="bi bi-envelope fs-3 d-block mb-2" />
            <span className="fw-medium">Add an outreach template</span>
            <span className="small text-muted d-block">
              Paste an email you'd actually send
            </span>
          </button>
        ) : (
          <div className="list-flush">
            {emailTemplates.map((t) => (
              <DocRow
                key={t.id}
                doc={t}
                icon="bi-envelope"
                meta={`${EMAIL_KIND_LABELS[t.kind] || t.kind} · Added ${formatDate(t.uploadedAt)}${
                  t.attachResume
                    ? ` · Attaches ${documents.resumes.find((r) => r.id === t.resumeId)?.name || "a resume"}`
                    : ""
                }`}
                onDefault={() => setDefaultDocument("emailTemplates", t.id)}
                onRemove={() => removeDocument("emailTemplates", t.id)}
                onOpen={() => setEmailEditor({ ...t, readOnly: true })}
                onEdit={() => setEmailEditor({ ...t })}
              />
            ))}
          </div>
        )}
      </div>

      <Modal
        show={!!emailEditor}
        onClose={() => setEmailEditor(null)}
        title={
          emailEditor?.readOnly ? emailEditor.name : "Outreach email template"
        }
        size="modal-lg"
        footer={
          emailEditor?.readOnly ? (
            <>
              <button
                className="btn btn-light"
                onClick={() => setEmailEditor(null)}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() =>
                  setEmailEditor((ed) => ({ ...ed, readOnly: false }))
                }
              >
                <i className="bi bi-pencil me-1" /> Edit
              </button>
            </>
          ) : (
            <>
              <button
                className="btn btn-light"
                onClick={() => setEmailEditor(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={saveEmailTemplate}
                disabled={
                  savingEmailTemplate ||
                  !emailEditor?.name.trim() ||
                  !emailEditor?.content.trim()
                }
              >
                {savingEmailTemplate && (
                  <span className="spinner-border spinner-border-sm me-2" />
                )}
                Save template
              </button>
            </>
          )
        }
      >
        {emailEditor && (
          <>
            {!emailEditor.readOnly && (
              <>
                <div className="mb-3">
                  <label className="form-label small fw-medium">Name</label>
                  <input
                    className="form-control"
                    placeholder="e.g. Short and direct"
                    value={emailEditor.name}
                    onChange={(e) =>
                      setEmailEditor((ed) => ({ ...ed, name: e.target.value }))
                    }
                    autoFocus
                  />
                </div>
                <div className="mb-3">
                  <label
                    className="form-label small fw-medium"
                    htmlFor="email-template-kind"
                  >
                    For
                  </label>
                  <select
                    id="email-template-kind"
                    className="form-select"
                    value={emailEditor.kind}
                    onChange={(e) =>
                      setEmailEditor((ed) => ({ ...ed, kind: e.target.value }))
                    }
                  >
                    {Object.entries(EMAIL_KIND_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="email-template-attach"
                      checked={Boolean(emailEditor.attachResume)}
                      disabled={documents.resumes.length === 0}
                      onChange={(e) =>
                        setEmailEditor((ed) => ({
                          ...ed,
                          attachResume: e.target.checked,
                          resumeId:
                            ed.resumeId ||
                            documents.resumes.find((r) => r.isDefault)?.id ||
                            documents.resumes[0]?.id ||
                            "",
                        }))
                      }
                    />
                    <label
                      className="form-check-label small fw-medium"
                      htmlFor="email-template-attach"
                    >
                      Attach resume
                    </label>
                  </div>
                  {documents.resumes.length === 0 ? (
                    <div className="form-text">
                      Upload a resume above first, then you can attach it here.
                    </div>
                  ) : emailEditor.attachResume ? (
                    <div className="mt-2">
                      <label
                        className="form-label small fw-medium"
                        htmlFor="email-template-resume"
                      >
                        Resume to attach
                      </label>
                      <select
                        id="email-template-resume"
                        className="form-select"
                        value={emailEditor.resumeId || ""}
                        onChange={(e) =>
                          setEmailEditor((ed) => ({
                            ...ed,
                            resumeId: e.target.value,
                          }))
                        }
                      >
                        {documents.resumes.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                            {r.isDefault ? " (default)" : ""}
                          </option>
                        ))}
                      </select>
                      <div className="form-text">
                        Every email written from this template comes with this
                        resume attached when you use “Send email” in Plateo.
                      </div>
                    </div>
                  ) : null}
                </div>
              </>
            )}
            {emailEditor.readOnly && (
              <div className="small text-muted mb-2">
                {EMAIL_KIND_LABELS[emailEditor.kind] || emailEditor.kind}
                {emailEditor.attachResume
                  ? ` · Attaches ${documents.resumes.find((r) => r.id === emailEditor.resumeId)?.name || "a resume"}`
                  : ""}
              </div>
            )}
            <label className="form-label small fw-medium">Template</label>
            <textarea
              className="form-control letter-editor mb-2"
              rows={10}
              readOnly={emailEditor.readOnly}
              placeholder={
                "Hi {{recipient_name}},\n\nI applied for the {{job_title}} role at {{company}}…"
              }
              value={emailEditor.content}
              onChange={(e) =>
                setEmailEditor((ed) => ({ ...ed, content: e.target.value }))
              }
            />
            {!emailEditor.readOnly && (
              <div className="small text-muted">
                Click to insert a placeholder:{" "}
                {EMAIL_PLACEHOLDERS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className="tag border-0 me-1 mb-1"
                    onClick={() =>
                      setEmailEditor((ed) => ({
                        ...ed,
                        content: `${ed.content}{{${p}}}`,
                      }))
                    }
                  >
                    {`{{${p}}}`}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </Modal>

      <Modal
        show={!!editor}
        onClose={() => setEditor(null)}
        title={editor?.readOnly ? editor.name : "Cover letter template"}
        size="modal-lg"
        footer={
          editor?.readOnly ? (
            <>
              <button className="btn btn-light" onClick={() => setEditor(null)}>
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setEditor((ed) => ({ ...ed, readOnly: false }))}
              >
                <i className="bi bi-pencil me-1" /> Edit
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-light" onClick={() => setEditor(null)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={saveTemplate}
                disabled={
                  savingTemplate ||
                  !editor?.name.trim() ||
                  !editor?.content.trim()
                }
              >
                {savingTemplate && (
                  <span className="spinner-border spinner-border-sm me-2" />
                )}
                Save template
              </button>
            </>
          )
        }
      >
        {editor && (
          <>
            {!editor.readOnly && (
              <div className="mb-3">
                <label className="form-label small fw-medium">Name</label>
                <input
                  className="form-control"
                  placeholder="e.g. Tech companies"
                  value={editor.name}
                  onChange={(e) =>
                    setEditor((ed) => ({ ...ed, name: e.target.value }))
                  }
                  autoFocus
                />
              </div>
            )}
            <label className="form-label small fw-medium">Template</label>
            <textarea
              className="form-control letter-editor mb-2"
              rows={12}
              readOnly={editor.readOnly}
              placeholder="Dear {{hiring_manager}},&#10;&#10;I'm excited to apply for the {{job_title}} role at {{company}}…"
              value={editor.content}
              onChange={(e) =>
                setEditor((ed) => ({ ...ed, content: e.target.value }))
              }
            />
            {!editor.readOnly && (
              <div className="small text-muted">
                Click to insert a placeholder:{" "}
                {PLACEHOLDERS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className="tag border-0 me-1 mb-1"
                    onClick={() =>
                      setEditor((ed) => ({
                        ...ed,
                        content: `${ed.content}{{${p}}}`,
                      }))
                    }
                  >
                    {`{{${p}}}`}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
}

export default Documents;
